// Cell labels (visuals §5; backlog R3-1): drawn right after each cell's body, from the text
// bitmap cache (Ubuntu 400, white with a black stroke).
// - name: font 0.571 × r, centred on the cell (also when the mass is shown); no minimum size and
//   no maximum width (long names overflow, as in agar); skipped below a 3 CSS px font (perf);
// - mass: font 0.286 × r (minimum 15.7 u), centred at cy + 0.44 r, `floor(mass)` refreshed at
//   most every 100 ms per cell; on every cell when "Show mass" is on.
// Cell rooms are free rooms: no value labels (the agar.io money layer was removed in lane X).

import { DEFAULT_RAD_Q, massOf } from '@ballio/shared';
import {
  type Frame,
  toScreenX,
  toScreenY,
  useScreenTransform,
  useWorldTransform,
} from './frame.js';
import {
  drawTextDirect,
  TEXT_CACHE_MAX_STEP,
  type TextCache,
  textSizeForStep,
  type TextSprite,
  type TextTheme,
} from './text-cache.js';

export const LABEL = Object.freeze({
  nameFontPerR: 0.571,
  massFontPerR: 0.286,
  massMinFontU: 15.7,
  massDyPerR: 0.44,
  /** Labels whose font is smaller than this on screen (CSS px) are skipped (perf guard). */
  minFontPx: 3,
  massRefreshMs: 100,
});

/** Name font size in world units: 0.571 × r (visuals §5). */
export function nameFontU(r: number): number {
  return LABEL.nameFontPerR * r;
}

/**
 * Mass the radius quantisation can hide (protocol §2: r is rounded to DEFAULT_RAD_Q steps, so it
 * is off by at most Δ = radQ/2), added before the floor so a mass-10 spawn (r 31.62 → 31.5 on the
 * wire) reads 10 like the score, not 9: `(2·r·Δ + Δ²)/100`, the largest mass the rounding can
 * hide. A full step (the first version) over-read: mass 400 showed 401.
 */
export function massLabelSlack(r: number, radQ: number = DEFAULT_RAD_Q): number {
  const d = radQ / 2;
  return r > 0 ? (2 * r * d + d * d) / 100 : 0;
}

/**
 * Own cells' mass label factor: the exact OWN score (floor Σ mass) over the Σ mass their drawn
 * radii give, so the own labels add up to the score box (a single cell reads exactly the score)
 * instead of carrying the wire rounding (review: mass 400 read 401). 0 = no factor (no score or
 * no own cells, or a sum that disagrees by more than 5 %, e.g. mid-merge).
 */
export function ownMassScale(score: number, own: readonly { readonly r: number }[]): number {
  if (!(score > 0) || own.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < own.length; i++) {
    const r = (own[i] as { r: number }).r;
    if (r > 0) sum += massOf(r);
  }
  if (!(sum > 0)) return 0;
  const k = score / sum;
  return k > 0.95 && k < 1.05 ? k : 0;
}

/** Mass font size in world units: max(0.286 × r, 15.7). */
export function massFontU(r: number): number {
  return Math.max(LABEL.massFontPerR * r, LABEL.massMinFontU);
}

/** Mass label centre: cy + 0.44 r. */
export function massLabelY(cy: number, r: number): number {
  return cy + LABEL.massDyPerR * r;
}

/** How far a name of `length` characters can reach from the centre (world units; culling). */
export function nameReachU(r: number, length: number): number {
  return Math.max(r, nameFontU(r) * 0.32 * Math.max(1, length));
}

/** One cell's label inputs (drawn position and radius). */
export interface LabelTarget {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

interface MassLabel {
  value: number;
  atMs: number;
  seenMs: number;
}

export class LabelPainter {
  private readonly massLabels = new Map<number, MassLabel>();
  private lastSweepMs = 0;

  /**
   * Name and mass labels of one cell (world transform in and out). `name` "" or `showName`
   * false draws no name. `massScale` > 0 (own cells, `ownMassScale`) labels `mass × massScale`;
   * otherwise the radius mass plus the rounding slack.
   */
  draw(
    f: Frame,
    cell: LabelTarget,
    name: string,
    showName: boolean,
    showMass: boolean,
    textCache: TextCache,
    massScale = 0,
  ): void {
    const r = cell.r;
    const zoom = f.zoom;
    const minU = LABEL.minFontPx / zoom;
    const key = f.theme.key;
    if (showName && name !== '') {
      const fontU = nameFontU(r);
      if (fontU >= minU) {
        this.label(f, textCache, key, name, -1, cell.x, cell.y, fontU);
        f.stats.nameDraws++;
      }
    }
    if (showMass) {
      const fontU = massFontU(r);
      if (fontU >= minU) {
        const mass = massScale > 0 ? massOf(r) * massScale + 1e-6 : massOf(r) + massLabelSlack(r);
        const value = this.massLabelValue(cell.id, mass, f.nowMs);
        this.label(f, textCache, key, '', value, cell.x, massLabelY(cell.y, r), fontU);
        f.stats.massDraws++;
      }
    }
  }

  /** Forget per-cell label state for cells unseen for a while (call once per frame). */
  endFrame(nowMs: number): void {
    if (nowMs - this.lastSweepMs < 2000) return;
    this.lastSweepMs = nowMs;
    for (const [id, label] of this.massLabels) {
      if (nowMs - label.seenMs > 1000) this.massLabels.delete(id);
    }
  }

  /** The integer a cell's mass label shows: refreshed at most every 100 ms. */
  massLabelValue(id: number, mass: number, nowMs: number): number {
    const value = Math.floor(mass);
    const label = this.massLabels.get(id);
    if (!label) {
      this.massLabels.set(id, { value, atMs: nowMs, seenMs: nowMs });
      return value;
    }
    label.seenMs = nowMs;
    if (value !== label.value && nowMs - label.atMs >= LABEL.massRefreshMs) {
      label.value = value;
      label.atMs = nowMs;
    }
    return label.value;
  }

  /**
   * One text label with a `fontU` world-unit font centred at (x, y): a cached bitmap scaled to
   * size (its height is 1.4 × font), or direct text when bigger than the cache renders.
   */
  private label(
    f: Frame,
    textCache: TextCache,
    key: TextTheme,
    text: string,
    mass: number,
    x: number,
    y: number,
    fontU: number,
  ): void {
    const { ctx } = f;
    const px = fontU * f.scale; // backing px
    if (px > textSizeForStep(TEXT_CACHE_MAX_STEP) * 1.15) {
      useScreenTransform(f);
      drawTextDirect(
        ctx,
        mass >= 0 ? String(mass) : text,
        toScreenX(f, x),
        toScreenY(f, y),
        px,
        textCache.themeColors(key),
      );
      useWorldTransform(f);
      return;
    }
    const sprite: TextSprite =
      mass >= 0 ? textCache.mass(mass, px, key) : textCache.name(text, px, key);
    const k = fontU / sprite.renderedPx; // world units per bitmap px
    const w = sprite.width * k;
    const h = sprite.height * k;
    ctx.drawImage(sprite.canvas, x - w / 2, y - h / 2, w, h);
  }
}
